import Vue from "vue";
import VueRouter from "vue-router";
import routes, { NOT_FOUND_ROUTE } from "./routes";
import { getAuthToken } from "../utils/auth";
import store from "../store";
import { buildRoutes } from "./dynamicRoutes";
import { hasPerm } from "../utils/permission";

Vue.use(VueRouter);

const router = new VueRouter({ mode: "history", routes });

const LOGIN_PATH = "/client/loginTest";
const FORBIDDEN_PATH = "/client/forbidden";

// meta.perm 声明式页面级权限:
// - 后端在 sys_menu 上给 MENU 节点配 perms 字符串,dynamicRoutes.buildRoutes 会把它写到
//   route.meta.perm。
// - 用户 perms 不含该码时,直接跳 /client/forbidden,避免像老页面那样 mounted() 就自动
//   触发查询导致按钮级 v-perm 形同虚设。
// - 数组表示 OR 匹配(任一命中即可通过)。
function passesRoutePerm(to) {
  const need = to.meta && to.meta.perm;
  if (!need) return true;
  const codes = Array.isArray(need) ? need : [need];
  return codes.some(hasPerm);
}

/**
 * 把后端下发的菜单树 addRoute 到 router 里,并把兜底 404 追加在最后。
 * 幂等:重复调用会先清光已挂载的动态路由再重新挂,方便"重新登录了不同角色"这种场景。
 *
 * 注意 vue-router 3 的 addRoute 没有直接的 removeRoute API。这里通过重建整个 matcher
 * 的做法保持简单:每次 mount 前重新 new 一个匹配器。
 */
export function mountDynamicRoutes() {
  // 重置 matcher — 相当于把之前动态挂载的路由都清掉,只留静态白名单
  router.matcher = new VueRouter({ mode: "history", routes }).matcher;

  const dyn = buildRoutes(store.state.user.menuTree);
  dyn.forEach((r) => router.addRoute(r));
  router.addRoute(NOT_FOUND_ROUTE);

  store.commit("user/SET_ROUTES_MOUNTED", true);
}

router.beforeEach(async (to, from, next) => {
  // 白名单直接放行 (登录页、403、404)
  if (to.meta && to.meta.public) {
    return next();
  }
  if (to.path === LOGIN_PATH) {
    return next();
  }

  // 未登录 → 登录页
  if (!getAuthToken()) {
    return next({ path: LOGIN_PATH, query: { redirect: to.fullPath } });
  }

  // 已登录但动态路由还没挂 (刷新页面时首次导航)
  if (!store.state.user.routesMounted) {
    try {
      if (!store.state.user.menusLoaded) {
        await store.dispatch("user/loadMenus");
      }
      mountDynamicRoutes();
      // 重新导航一次让新挂的 route 生效
      return next({ ...to, replace: true });
    } catch (e) {
      return next({ path: LOGIN_PATH, query: { redirect: to.fullPath } });
    }
  }

  if (!passesRoutePerm(to)) {
    return next({ path: FORBIDDEN_PATH, replace: true });
  }

  next();
});

const VueRouterPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(to) {
  return VueRouterPush.call(this, to).catch((err) => err);
};

export default router;
